import React, { useState } from 'react';
import API_BASE from './apiBase';
import Swal from 'sweetalert2';
import '../css/App.css';

function TrialConsentForm({ onClose = () => {} }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [organization, setOrganization] = useState('');
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUsername, setSmtpUsername] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');
  const [smtpUseTls, setSmtpUseTls] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [focused, setFocused] = useState('');

  const validate = () => {
    if (!name.trim()) return 'Name is required';
    if (!organization.trim()) return 'Organization name is required';
    if (!email.trim() || !/^\S+@\S+\.\S+$/.test(email)) return 'Valid email is required';
    // Phone: if provided, require 10 digits starting with 6-9
    if (phone && !/^[6-9]\d{9}$/.test(phone)) return 'Phone must be a 10-digit number starting with 6,7,8 or 9';
    if (!smtpHost.trim()) return 'SMTP host is required';
    if (!smtpUsername.trim()) return 'SMTP username is required';
    if (!smtpPassword.trim()) return 'SMTP password / app password is required';
    if (!smtpFrom.trim() || !/^\S+@\S+\.\S+$/.test(smtpFrom)) return 'Valid SMTP From address is required';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v) {
      try {
        await Swal.fire({ icon: 'warning', title: 'Validation', text: v });
      } catch (e) {}
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/user/trial-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          message,
          organization,
          smtp_host: smtpHost,
          smtp_port: parseInt(smtpPort || '587', 10),
          smtp_username: smtpUsername,
          smtp_password: smtpPassword,
          smtp_use_tls: !!smtpUseTls,
          smtp_from: smtpFrom
        })
      });
      if (!res.ok) {
        const ct = res.headers.get('content-type') || '';
        let msg = 'Server error';
        try {
          if (ct.indexOf('application/json') !== -1) {
            const j = await res.json();
            msg = j.detail || j.message || JSON.stringify(j) || msg;
          } else {
            const txt = await res.text();
            msg = txt || msg;
          }
        } catch (e) {}
        if (res.status === 409) {
          throw new Error('A trial request or tenant with that organization name or email already exists.');
        }
        throw new Error(msg);
      }
      await res.json();
      // Clear submitting state immediately so UI is responsive even if alerts hang
      setSubmitting(false);
      try {
        // do not await here so we don't block UI updates
        Swal.fire({ icon: 'success', title: 'Request submitted', text: 'Our sales team will contact you.' });
      } catch (e) {}
      try {
        onClose();
      } catch (e) {}
    } catch (err) {
      console.error('Trial consent submit error', err);
      try {
        await Swal.fire({ icon: 'error', title: 'Submit failed', text: (err.message || String(err)) });
      } catch (e) {}
    } finally {
      // ensure submitting is false in case of errors
      setSubmitting(false);
    }
  };

  return (
    <div className="tcf-overlay">
      <div className="tcf-card">

        {/* Close button */}
        <button className="tcf-close" onClick={onClose} aria-label="Close">
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path d="M14 4L4 14M4 4l10 10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>
        </button>

        {/* Header */}
        <div className="tcf-header">
          <div className="tcf-badge">Limited Access</div>
          <h2 className="tcf-title">Start Your Free License </h2>
          <p className="tcf-subtitle">Fill in your details and our team will reach out within 24 hours.</p>
        </div>

        {/* Divider */}
        <div className="tcf-divider" />

        {/* Form */}
        <form onSubmit={handleSubmit} className="tcf-form">

          <div className={`tcf-field ${focused === 'name' ? 'tcf-field--focused' : ''} ${name ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-name">
              <svg className="tcf-icon" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="7" r="3.5" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M3 17c0-3.314 3.134-6 7-6s7 2.686 7 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Full Name <span className="tcf-required">*</span>
            </label>
            <input
              id="tcf-name"
              className="tcf-input"
              type="text"
              value={name}
              placeholder="Jane Doe"
              onChange={e => setName(e.target.value)}
              onFocus={() => setFocused('name')}
              onBlur={() => setFocused('')}
              required
            />
          </div>

          <div className={`tcf-field ${focused === 'organization' ? 'tcf-field--focused' : ''} ${organization ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-organization">
              <svg className="tcf-icon" viewBox="0 0 20 20" fill="none">
                <path d="M3 6h14v8H3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M3 6l7 4 7-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Organization Name <span className="tcf-required">*</span>
            </label>
            <input
              id="tcf-organization"
              className="tcf-input"
              type="text"
              value={organization}
              placeholder="Your company or organization"
              onChange={e => setOrganization(e.target.value)}
              onFocus={() => setFocused('organization')}
              onBlur={() => setFocused('')}
              required
            />
          </div>

          <div className={`tcf-field ${focused === 'email' ? 'tcf-field--focused' : ''} ${email ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-email">
              <svg className="tcf-icon" viewBox="0 0 20 20" fill="none">
                <rect x="2" y="5" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.5"/>
                <path d="M2 7l8 5 8-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Email Address <span className="tcf-required">*</span>
            </label>
            <input
              id="tcf-email"
              className="tcf-input"
              type="email"
              value={email}
              placeholder="jane@company.com"
              onChange={e => setEmail(e.target.value)}
              onFocus={() => setFocused('email')}
              onBlur={() => setFocused('')}
              required
            />
          </div>

          <div className={`tcf-field ${focused === 'phone' ? 'tcf-field--focused' : ''} ${phone ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-phone">
              <svg className="tcf-icon" viewBox="0 0 20 20" fill="none">
                <path d="M4 2h4l1.5 4-2 1.5a10 10 0 004 4L13 9.5l4 1.5v4a2 2 0 01-2 2A14 14 0 012 4a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
              Phone <span className="tcf-required">*</span>
            </label>
            <input
              id="tcf-phone"
              className="tcf-input"
              type="tel"
              value={phone}
              placeholder="10-digit mobile number"
              maxLength={10}
              onChange={e => {
                // keep only digits and limit to 10
                const cleaned = (e.target.value || '').replace(/\D+/g, '').slice(0, 10);
                setPhone(cleaned);
              }}
              onFocus={() => setFocused('phone')}
              onBlur={() => setFocused('')}
            />
          </div>

          <div className={`tcf-field ${focused === 'message' ? 'tcf-field--focused' : ''} ${message ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-message">
              <svg className="tcf-icon" viewBox="0 0 20 20" fill="none">
                <path d="M2 4a2 2 0 012-2h12a2 2 0 012 2v9a2 2 0 01-2 2H6l-4 3V4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
              </svg>
              Message <span className="tcf-required">*</span>
            </label>
            <textarea
              id="tcf-message"
              className="tcf-textarea"
              value={message}
              placeholder="Tell us about your use case..."
              onChange={e => setMessage(e.target.value)}
              onFocus={() => setFocused('message')}
              onBlur={() => setFocused('')}
              rows={3}
            />
          </div>

          {/* SMTP configuration fields (required) */}
          <div className={`tcf-field ${focused === 'smtpHost' ? 'tcf-field--focused' : ''} ${smtpHost ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-smtp-host">SMTP Host <span className="tcf-required">*</span></label>
            <input
              id="tcf-smtp-host"
              className="tcf-input"
              type="text"
              value={smtpHost}
              placeholder="smtp.gmail.com"
              onChange={e => setSmtpHost(e.target.value)}
              onFocus={() => setFocused('smtpHost')}
              onBlur={() => setFocused('')}
              required
            />
          </div>

          <div className={`tcf-field ${focused === 'smtpPort' ? 'tcf-field--focused' : ''} ${smtpPort ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-smtp-port">SMTP Port <span className="tcf-required">*</span></label>
            <input
              id="tcf-smtp-port"
              className="tcf-input"
              type="number"
              value={smtpPort}
              placeholder="587"
              onChange={e => setSmtpPort(e.target.value)}
              onFocus={() => setFocused('smtpPort')}
              onBlur={() => setFocused('')}
              required
            />
          </div>

          <div className={`tcf-field ${focused === 'smtpUsername' ? 'tcf-field--focused' : ''} ${smtpUsername ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-smtp-username">SMTP Username <span className="tcf-required">*</span></label>
            <input
              id="tcf-smtp-username"
              className="tcf-input"
              type="text"
              value={smtpUsername}
              placeholder="your@email.com"
              onChange={e => setSmtpUsername(e.target.value)}
              onFocus={() => setFocused('smtpUsername')}
              onBlur={() => setFocused('')}
              required
            />
          </div>

          <div className={`tcf-field ${focused === 'smtpPassword' ? 'tcf-field--focused' : ''} ${smtpPassword ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-smtp-password">SMTP Password / App Password <span className="tcf-required">*</span></label>
            <input
              id="tcf-smtp-password"
              className="tcf-input"
              type="password"
              value={smtpPassword}
              placeholder="App password or SMTP password"
              onChange={e => setSmtpPassword(e.target.value)}
              onFocus={() => setFocused('smtpPassword')}
              onBlur={() => setFocused('')}
              required
            />
          </div>

          <div className={`tcf-field ${focused === 'smtpFrom' ? 'tcf-field--focused' : ''} ${smtpFrom ? 'tcf-field--filled' : ''}`}>
            <label className="tcf-label" htmlFor="tcf-smtp-from">From Address (SMTP) <span className="tcf-required">*</span></label>
            <input
              id="tcf-smtp-from"
              className="tcf-input"
              type="email"
              value={smtpFrom}
              placeholder="no-reply@company.com"
              onChange={e => setSmtpFrom(e.target.value)}
              onFocus={() => setFocused('smtpFrom')}
              onBlur={() => setFocused('')}
              required
            />
          </div>

          <div className="tcf-field">
            <label className="tcf-label" htmlFor="tcf-smtp-use-tls">
              <input
                id="tcf-smtp-use-tls"
                type="checkbox"
                checked={smtpUseTls}
                onChange={e => setSmtpUseTls(e.target.checked)}
                style={{ marginRight: 8 }}
              />
              Use TLS (recommended)
            </label>
          </div>

          <div className="tcf-actions">
            <button
              type="submit"
              className={`tcf-btn-primary ${submitting ? 'tcf-btn-primary--loading' : ''}`}
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <span className="tcf-spinner" />
                  Submitting…
                </>
              ) : (
                <>
                  Request Access
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{marginLeft: 6}}>
                    <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </>
              )}
            </button>
            <button type="button" className="tcf-btn-ghost" onClick={onClose}>
              Cancel
            </button>
          </div>

          <p className="tcf-footnote">
            By submitting, you agree to be contacted by our sales team. No spam, ever.
          </p>
        </form>
      </div>
    </div>
  );
}

export default TrialConsentForm;